import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { submitKieTask, pollKieTask, getKieApiKey, getKieCallbackUrl, uploadFileToKie, submitKieTaskViaProxy } from "@/lib/kie-service";
import { characterService } from "@/lib/character-service";

type ServiceType = "photo" | "video" | "music" | "prompt_ai";
type GenerationStatus = "pending" | "processing" | "completed" | "failed";

interface GenerationResult {
  success: boolean;
  logId?: string;
  resultUrl?: string;
  error?: string;
  message?: string;
}

export const useGeneration = () => {
  const { user, profile, refreshProfile } = useAuth();
  const { t } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  const checkCredits = (requiredCredits: number): boolean => {
    if (!profile) {
      toast.error(t("กรุณาเข้าสู่ระบบ", "Please log in"));
      return false;
    }

    if (profile.credits < requiredCredits) {
      toast.error(
        t(
          `เครดิตไม่เพียงพอ ต้องการ ${requiredCredits} เครดิต คุณมี ${profile.credits} เครดิต`,
          `Insufficient credits. Need ${requiredCredits}, you have ${profile.credits}`
        )
      );
      return false;
    }

    return true;
  };

  const deductCredits = async (
    amount: number,
    serviceType: ServiceType,
    model: string,
    prompt: string
  ): Promise<{ success: boolean; logId?: string; error?: string }> => {
    if (!user || !profile) {
      return { success: false, error: "User not authenticated" };
    }

    try {
      // Use server-side RPC function to prevent client-side manipulation
      const { data, error } = await supabase.rpc('deduct_credits_for_generation' as any, {
        p_user_id: user.id,
        p_credits_required: amount,
        p_service_type: serviceType,
        p_model: model,
        p_prompt: prompt
      });

      if (error) {
        console.error("RPC Error deducting credits:", error);
        toast.error(t("เกิดข้อผิดพลาดในการหักเครดิต", "Error deducting credits"));
        return { success: false, error: error.message };
      }

      const result = data as { success: boolean; log_id?: string; error?: string; remaining_credits?: number };

      if (!result.success) {
        toast.error(t(
          result.error === "Insufficient credits"
            ? "เครดิตไม่เพียงพอ"
            : "เกิดข้อผิดพลาดในการหักเครดิต",
          result.error || "Error deducting credits"
        ));
        return { success: false, error: result.error };
      }

      // Refresh profile to show updated credits
      await refreshProfile();

      return {
        success: true,
        logId: result.log_id
      };
    } catch (err) {
      console.error("Exception deducting credits:", err);
      return { success: false, error: String(err) };
    }
  };

  const refundCredits = async (logId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Use server-side RPC function for refund
      const { data, error } = await supabase.rpc('refund_credits_for_failed_generation' as any, {
        p_log_id: logId
      });

      if (error) {
        console.error("RPC Error refunding credits:", error);
        return false;
      }

      const result = data as { success: boolean; error?: string };

      if (!result.success) {
        console.error("Refund failed:", result.error);
        return false;
      }

      await refreshProfile();
      return true;
    } catch (err) {
      console.error("Exception refunding credits:", err);
      return false;
    }
  };

  const createLog = async (
    serviceType: ServiceType,
    model: string,
    prompt: string,
    creditsUsed: number
  ): Promise<string | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from("generation_logs")
      .insert({
        user_id: user.id,
        service_type: serviceType,
        model,
        prompt,
        credits_used: creditsUsed,
        status: "pending" as GenerationStatus,
      })
      .select("id")
      .single();

    if (error) {
      console.error("Error creating log:", error);
      return null;
    }

    return data.id;
  };

  const updateLogStatus = async (
    logId: string,
    status: GenerationStatus,
    resultUrl?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    console.log('📝 updateLogStatus called:', { logId, status, resultUrl: resultUrl?.substring(0, 50) + '...', errorMessage });
    console.log('👤 Current user ID for update:', user?.id);

    if (!user) {
      console.error("❌ No user session for update!");
      return false;
    }

    // Refresh session to ensure auth token is valid
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('🔐 Session check:', sessionData?.session?.user?.id);

    if (!sessionData?.session) {
      console.error("❌ No active session!");
      return false;
    }

    const updateData: { status: GenerationStatus; result_url?: string; completed_at?: string; error_message?: string } = {
      status,
    };

    if (resultUrl) {
      updateData.result_url = resultUrl;
    }

    if (status === "completed" || status === "failed") {
      updateData.completed_at = new Date().toISOString();
    }

    if (status === "failed" && errorMessage) {
      updateData.error_message = errorMessage;
    }

    // console.log('📝 Update data:', updateData);

    // Try update with RLS
    const { data, error } = await supabase
      .from("generation_logs")
      .update(updateData)
      .eq("id", logId)
      .eq("user_id", user.id)
      .select();

    if (error) {
      console.error("❌ Error updating log:", error);
      return false;
    }

    // console.log('✅ Update successful, affected rows:', data?.length || 0);

    // If RLS blocked the update, try using RPC function (SECURITY DEFINER)
    if (!data || data.length === 0) {
      // console.warn("⚠️ RLS blocked update! Trying RPC function...");

      const completedAt = (status === "completed" || status === "failed")
        ? new Date().toISOString()
        : null;

      const { data: rpcResult, error: rpcError } = await supabase.rpc('update_generation_log_status' as any, {
        p_log_id: logId,
        p_user_id: user.id,
        p_status: status,
        p_result_url: resultUrl || null,
        p_completed_at: completedAt,
        p_error_message: errorMessage || null
      });

      if (rpcError) {
        console.error("❌ RPC update failed:", rpcError.message);
      } else {
        // console.log('🔄 RPC update result:', rpcResult);
        if (rpcResult === true) {
          return true;
        }
      }

      // Log detailed info for debugging
      const { data: logData, error: fetchError } = await supabase
        .from("generation_logs")
        .select("id, user_id, status")
        .eq("id", logId)
        .single();

      if (fetchError) {
        console.error("❌ Could not fetch log:", fetchError);
      } else {
        // console.log("📋 Log data:", logData);
        if (logData.user_id !== user.id) {
          console.error("❌ User ID mismatch! Log user:", logData.user_id, "Current user:", user.id);
        }
      }

      return false;
    }

    return true;
  };

  const generate = async (
    serviceType: ServiceType,
    model: string,
    prompt: string,
    creditsRequired: number,
    options: Record<string, any> = {}
  ): Promise<GenerationResult> => {
    if (!user || !profile) {
      return { success: false, error: "Not authenticated" };
    }

    // Check credits
    if (!checkCredits(creditsRequired)) {
      return { success: false, error: "Insufficient credits" };
    }

    const apiKey = await getKieApiKey();
    if (!apiKey) {
      toast.error(t("กรุณาตั้งค่า API Key ในหน้า Admin", "Please configure API Key in Admin page"));
      return { success: false, error: "Missing API Key" };
    }

    // 0. Resolve character aliases (@@name)
    let finalPrompt = prompt;
    let autoCharacterId: string | null = null;

    if (model.toLowerCase().includes("sora")) {
      const resolved = await characterService.resolvePromptAliases(prompt);
      finalPrompt = resolved.prompt;
      autoCharacterId = resolved.characterId;
      console.log('🔍 Resolved Alias:', { original: prompt, final: finalPrompt, characterId: autoCharacterId });
    }

    setIsGenerating(true);
    setGenerationProgress(5);
    setStatusMessage(t("กำลังส่งข้อมูล...", "Submitting request..."));

    const { magic, displayPrompt, magicCategory, referenceImage, ...apiOptions } = options;
    let logId: string | null = null;
    try {
      // 1. Deduct credits using server-side RPC (creates log atomically)
      console.log('💸 Deducting credits via RPC:', creditsRequired);
      const deductResult = await deductCredits(creditsRequired, serviceType, model, prompt);

      if (!deductResult.success) {
        console.warn('❌ Credit deduction failed:', deductResult.error);
        setIsGenerating(false);
        return { success: false, error: deductResult.error || "Failed to deduct credits" };
      }

      logId = deductResult.logId || null;
      console.log('📝 Log created via RPC:', logId);

      if (!logId) {
        setIsGenerating(false);
        return { success: false, error: "Failed to create log" };
      }

      if (magic) {
        try {
          await supabase
            .from("generation_logs")
            .update({
              is_magic: true,
              display_prompt: displayPrompt || "Magic Button",
              magic_category: magicCategory || null,
            })
            .eq("id", logId)
            .eq("user_id", user.id);
        } catch (metaError) {
          console.warn("Failed to update magic metadata:", metaError);
        }
      }

      // Update auto_retry flag and store request options for potential resubmission
      if (options.auto_retry) {
        try {
          // Store options needed for retry (excluding File objects)
          const retryOptions: Record<string, any> = {
            ratio: options.ratio,
            n_frames: options.n_frames,
            duration: options.duration,
            resolution: options.resolution,
            remove_watermark: options.remove_watermark,
            model: model,
            prompt: finalPrompt,
          };

          // Store image URL if available (after upload)
          if (options.referenceImage && typeof options.referenceImage === 'string') {
            retryOptions.referenceImage = options.referenceImage;
          }

          await supabase
            .from("generation_logs")
            .update({
              auto_retry: true,
              retry_count: 0,
              max_retries: options.max_retries ?? 20, // Use passed value or default to 20
              request_options: retryOptions,
            } as any)
            .eq("id", logId)
            .eq("user_id", user.id);
          console.log('🔄 Auto-retry enabled for log:', logId);
        } catch (autoRetryError) {
          console.warn("Failed to set auto_retry flag:", autoRetryError);
        }
      }

      // 2. Update to processing
      console.log('🔄 Updating status to processing...');
      setStatusMessage(t("หักเครดิตเรียบร้อย กำลังเริ่มสร้าง...", "Credits deducted. Starting generation..."));
      setGenerationProgress(10);
      const updatedToProcessing = await updateLogStatus(logId, "processing");
      if (!updatedToProcessing) {
        throw new Error("Failed to update log status to processing");
      }

      // 3. Handle Image Upload if referenceImage is provided
      if (options.referenceImage instanceof File) {
        console.log('📤 Uploading reference image to storage...');
        setStatusMessage(t("กำลังอัปโหลดรูปภาพ...", "Uploading image..."));
        setGenerationProgress(15);
        const file = options.referenceImage;
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('generation-inputs')
          .upload(filePath, file);

        if (uploadError) {
          console.error('❌ Upload error:', uploadError);
          // If bucket doesn't exist, it might fail. You can try 'samples' or 'payment-slips' as fallback if needed
          // but better to ensure bucket exists.
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }

        // Try signed URL first (works with private buckets), fallback to public URL
        let imageUrl: string;
        const { data: signedData } = await supabase.storage
          .from('generation-inputs')
          .createSignedUrl(filePath, 3600); // 1 hour expiry

        if (signedData?.signedUrl) {
          imageUrl = signedData.signedUrl;
          console.log('🔗 Using signed URL for image (private bucket support)');
        } else {
          // Fallback to public URL if signed URL fails
          const { data: { publicUrl } } = supabase.storage
            .from('generation-inputs')
            .getPublicUrl(filePath);
          imageUrl = publicUrl;
          console.log('🔗 Fallback to public URL for image');
        }

        // Track upload for reuse (stock gallery)
        try {
          const { data: { publicUrl: permanentUrl } } = supabase.storage
            .from('generation-inputs')
            .getPublicUrl(filePath);

          await supabase.from('user_uploads' as any).insert({
            user_id: user.id,
            file_path: filePath,
            file_name: file.name,
            file_size: file.size,
            file_type: 'image',
            public_url: permanentUrl,
          });
          console.log('📦 Upload tracked for reuse');
        } catch (trackError) {
          console.warn('Failed to track upload (non-critical):', trackError);
        }

        apiOptions.imageUrls = [imageUrl];
      } else if (typeof options.referenceImage === 'string' && options.referenceImage.startsWith('http')) {
        console.log('🔗 Using provided reference image URL:', options.referenceImage);
        apiOptions.imageUrls = [options.referenceImage];
      }

      // Handle Multiple Image Upload for Nano Banana Pro (up to 8 images)
      if (options.referenceImages && Array.isArray(options.referenceImages) && options.referenceImages.length > 0) {
        console.log(`📤 Uploading ${options.referenceImages.length} reference images for Nano Banana Pro...`);
        setStatusMessage(t(`กำลังอัปโหลดรูปภาพ ${options.referenceImages.length} ภาพ...`, `Uploading ${options.referenceImages.length} images...`));
        setGenerationProgress(15);

        const uploadedUrls: string[] = [];

        for (let i = 0; i < options.referenceImages.length; i++) {
          const file = options.referenceImages[i];
          const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}-${i}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('generation-inputs')
            .upload(filePath, file);

          if (uploadError) {
            console.error(`❌ Upload error for image ${i + 1}:`, uploadError);
            throw new Error(`Failed to upload image ${i + 1}: ${uploadError.message}`);
          }

          // Get signed URL for the uploaded image
          const { data: signedData } = await supabase.storage
            .from('generation-inputs')
            .createSignedUrl(filePath, 3600);

          if (signedData?.signedUrl) {
            uploadedUrls.push(signedData.signedUrl);
          } else {
            const { data: { publicUrl } } = supabase.storage
              .from('generation-inputs')
              .getPublicUrl(filePath);
            uploadedUrls.push(publicUrl);
          }

          // Update progress
          setGenerationProgress(15 + Math.round((i + 1) / options.referenceImages.length * 5));
        }

        console.log(`✅ Uploaded ${uploadedUrls.length} images successfully`);
        apiOptions.imageInputUrls = uploadedUrls;
      }

      // Handle Video Upload if referenceVideo is provided
      if (options.referenceVideo instanceof File) {
        console.log('📤 Uploading reference video to storage...', {
          fileName: options.referenceVideo.name,
          fileSize: options.referenceVideo.size,
          fileType: options.referenceVideo.type
        });
        setStatusMessage(t("กำลังอัปโหลดวิดีโอ...", "Uploading video..."));
        setGenerationProgress(18);
        const file = options.referenceVideo;
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'mp4';
        const fileName = `video-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        // Try multiple buckets in order of preference
        const bucketsToTry = ['generation-inputs', 'payment-slips'];
        let uploadBucket = '';
        let uploadError: any = null;

        for (const bucket of bucketsToTry) {
          console.log(`📦 Trying bucket: ${bucket}`);
          const result = await supabase.storage
            .from(bucket)
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (!result.error) {
            uploadBucket = bucket;
            console.log(`✅ Upload successful to bucket: ${bucket}`);
            break;
          } else {
            console.warn(`⚠️ Bucket ${bucket} failed:`, result.error.message);
            uploadError = result.error;
          }
        }

        if (!uploadBucket) {
          console.error('❌ All upload buckets failed:', uploadError);
          throw new Error(`Failed to upload video. Please contact admin to setup storage bucket.`);
        }

        // Try signed URL first (works with private buckets), fallback to public URL
        let videoUrl: string;
        const { data: signedData, error: signedError } = await supabase.storage
          .from(uploadBucket)
          .createSignedUrl(filePath, 3600); // 1 hour expiry - enough for KIE to process

        if (signedData?.signedUrl) {
          videoUrl = signedData.signedUrl;
          console.log('🔗 Using signed URL for video (private bucket support)');
        } else {
          // Fallback to public URL if signed URL fails
          console.warn('⚠️ Signed URL failed, falling back to public URL:', signedError?.message);
          const { data: { publicUrl } } = supabase.storage
            .from(uploadBucket)
            .getPublicUrl(filePath);
          videoUrl = publicUrl;
        }

        console.log('🔗 Video URL:', videoUrl);

        // Verify the URL is accessible
        try {
          const checkResponse = await fetch(videoUrl, { method: 'HEAD' });
          if (!checkResponse.ok) {
            console.warn('⚠️ Video URL may not be publicly accessible:', checkResponse.status);
          } else {
            console.log('✅ Video URL is accessible');
          }
        } catch (checkErr) {
          console.warn('⚠️ Could not verify video URL accessibility:', checkErr);
        }

        apiOptions.videoUrl = videoUrl;
      } else if (typeof options.referenceVideo === 'string' && options.referenceVideo.startsWith('http')) {
        console.log('🔗 Using provided video URL:', options.referenceVideo);
        apiOptions.videoUrl = options.referenceVideo;
      }

      const uploadSeedanceReferenceFile = async (
        file: File,
        fileType: "image" | "video" | "audio",
        index: number
      ): Promise<string> => {
        const fileExt = file.name.split('.').pop()?.toLowerCase() || (
          fileType === "audio" ? "mp3" : fileType === "video" ? "mp4" : "png"
        );
        const fileName = `${fileType}-${Date.now()}-${Math.random().toString(36).substring(7)}-${index}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('generation-inputs')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          throw new Error(`Failed to upload ${fileType}: ${uploadError.message}`);
        }

        const { data: signedData } = await supabase.storage
          .from('generation-inputs')
          .createSignedUrl(filePath, 3600);

        if (signedData?.signedUrl) {
          return signedData.signedUrl;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('generation-inputs')
          .getPublicUrl(filePath);
        return publicUrl;
      };

      if (model.toLowerCase().includes("seedance")) {
        const imageFiles: File[] = Array.isArray(options.referenceImages)
          ? options.referenceImages.filter((file: unknown): file is File => file instanceof File)
          : [];
        const videoFiles: File[] = Array.isArray(options.referenceVideos)
          ? options.referenceVideos.filter((file: unknown): file is File => file instanceof File)
          : [];
        const audioFiles: File[] = Array.isArray(options.referenceAudios)
          ? options.referenceAudios.filter((file: unknown): file is File => file instanceof File)
          : [];

        if (options.firstFrame instanceof File) {
          setStatusMessage(t("กำลังอัปโหลดภาพเริ่มต้น...", "Uploading first frame..."));
          apiOptions.firstFrameUrl = await uploadSeedanceReferenceFile(options.firstFrame, "image", 0);
        } else if (typeof options.firstFrame === "string" && options.firstFrame.startsWith("http")) {
          apiOptions.firstFrameUrl = options.firstFrame;
        }

        if (options.lastFrame instanceof File) {
          setStatusMessage(t("กำลังอัปโหลดภาพท้าย...", "Uploading last frame..."));
          apiOptions.lastFrameUrl = await uploadSeedanceReferenceFile(options.lastFrame, "image", 1);
        } else if (typeof options.lastFrame === "string" && options.lastFrame.startsWith("http")) {
          apiOptions.lastFrameUrl = options.lastFrame;
        }

        if (imageFiles.length > 0) {
          setStatusMessage(t("กำลังอัปโหลดภาพประกอบ...", "Uploading reference images..."));
          const uploaded = await Promise.all(imageFiles.map((file, index) => uploadSeedanceReferenceFile(file, "image", index + 2)));
          apiOptions.referenceImageUrls = [
            ...(apiOptions.referenceImageUrls || []),
            ...uploaded
          ];
        }

        if (videoFiles.length > 0) {
          setStatusMessage(t("กำลังอัปโหลดวิดีโอประกอบ...", "Uploading reference videos..."));
          apiOptions.referenceVideoUrls = await Promise.all(videoFiles.map((file, index) => uploadSeedanceReferenceFile(file, "video", index)));
        }

        if (audioFiles.length > 0) {
          setStatusMessage(t("กำลังอัปโหลดเสียงประกอบ...", "Uploading reference audio..."));
          apiOptions.referenceAudioUrls = await Promise.all(audioFiles.map((file, index) => uploadSeedanceReferenceFile(file, "audio", index)));
        }

        if (Array.isArray(options.referenceImageUrls)) {
          apiOptions.referenceImageUrls = [
            ...(apiOptions.referenceImageUrls || []),
            ...options.referenceImageUrls.filter((url: unknown): url is string => typeof url === "string" && url.startsWith("http"))
          ];
        }
        if (Array.isArray(options.referenceVideoUrls)) {
          apiOptions.referenceVideoUrls = options.referenceVideoUrls.filter((url: unknown): url is string => typeof url === "string" && url.startsWith("http"));
        }
        if (Array.isArray(options.referenceAudioUrls)) {
          apiOptions.referenceAudioUrls = options.referenceAudioUrls.filter((url: unknown): url is string => typeof url === "string" && url.startsWith("http"));
        }
      }

      // 4. Submit Task to Kie.ai
      let endpoint = "";
      let body: any = { prompt };
      const callBackUrl = await getKieCallbackUrl();

      if (serviceType === "photo") {
        if (model.toLowerCase().includes("gpt-image") || model.toLowerCase().includes("gpt4o")) {
          // Use generic jobs/createTask endpoint for these models
          endpoint = "/api/v1/jobs/createTask";

          // Map 1024x1024 back to 1:1 for the API
          const ratio = apiOptions.ratio === "1024x1024" ? "1:1" : (apiOptions.ratio || "1:1");

          body = {
            model: "gpt-image/1.5-text-to-image",
            input: {
              prompt: prompt,
              aspect_ratio: ratio,
              quality: "basic"
            }
          };
        } else if (model.toLowerCase().includes("gemini")) {
          endpoint = "/api/v1/gemini/generate";
          if (apiOptions.ratio) body.size = apiOptions.ratio;
        } else if (model.toLowerCase().includes("seedream")) {
          endpoint = "/api/v1/jobs/createTask";

          // Ensure ratio is valid enum, default to 1:1
          const validRatios = ['1:1', '4:3', '3:4', '16:9', '9:16', '2:3', '3:2', '21:9'];
          const ratio = apiOptions.ratio && validRatios.includes(apiOptions.ratio) ? apiOptions.ratio : '1:1';

          body = {
            model: "seedream/4.5-text-to-image",
            input: {
              prompt: prompt,
              aspect_ratio: ratio,
              quality: "basic"
            }
          };
        } else if (model.toLowerCase().includes("5 lite") || model.toLowerCase().includes("5-lite")) {
          endpoint = "/api/v1/jobs/createTask";

          // Ensure ratio is valid enum, default to 1:1
          const validRatios = ['1:1', '4:3', '3:4', '16:9', '9:16', '2:3', '3:2', '21:9'];
          const ratio = apiOptions.ratio && validRatios.includes(apiOptions.ratio) ? apiOptions.ratio : '1:1';

          body = {
            model: "seedream/5-lite-text-to-image",
            input: {
              prompt: prompt,
              aspect_ratio: ratio,
              quality: "basic"
            }
          };
        } else if (model.toLowerCase().includes("nannobanana") || model.toLowerCase().includes("nanobana") || model.toLowerCase().includes("nano banana")) {
          endpoint = "/api/v1/jobs/createTask";

          // Map ratio to allowed enums
          const validRatios = ['1:1', '9:16', '16:9', '3:4', '4:3', '3:2', '2:3', '5:4', '4:5', '21:9', 'auto'];
          const ratio = apiOptions.ratio && validRatios.includes(apiOptions.ratio) ? apiOptions.ratio : '1:1';

          // Check if this is Nano Banana Pro (with resolution)
          if (apiOptions.nanobanaPro) {
            // Nano Banana Pro model - supports 1K/2K/4K resolution and up to 8 reference images
            const resolution = apiOptions.resolution || "1K";
            const inputParams: Record<string, any> = {
              prompt: prompt,
              aspect_ratio: ratio,
              resolution: resolution,
              output_format: "png"
            };

            // Add image_input if multiple images were uploaded
            if (apiOptions.imageInputUrls && apiOptions.imageInputUrls.length > 0) {
              inputParams.image_input = apiOptions.imageInputUrls;
              console.log(`📸 Adding ${apiOptions.imageInputUrls.length} images to Nano Banana Pro request`);
            }

            body = {
              model: "nano-banana-pro",
              input: inputParams
            };
          } else if (apiOptions.nanoBananaEdit && apiOptions.imageUrls && apiOptions.imageUrls.length > 0) {
            // Nano Banana Edit model
            body = {
              model: "google/nano-banana-edit",
              input: {
                prompt: prompt,
                image_urls: apiOptions.imageUrls,
                image_size: ratio,
                output_format: "png"
              }
            };
          } else {
            // Nano Banana Generate model
            body = {
              model: "google/nano-banana",
              input: {
                prompt: prompt,
                image_size: ratio,
                output_format: "png"
              }
            };
          }
        } else {
          endpoint = "/api/v1/flux/kontext/generate";
          if (apiOptions.ratio) body.aspectRatio = apiOptions.ratio;
        }
      } else if (serviceType === "video") {
        if (model.toLowerCase().includes("veo")) {
          endpoint = "/api/v1/veo/generate";
          const { ratio, imageUrls, veoModel: veoModelVariant, veoFullHD: veoHD, ...rest } = apiOptions;

          // Veo 3.1 - supports veo3_fast and veo3_lite models
          // Generation types: TEXT_2_VIDEO or FIRST_AND_LAST_FRAMES_2_VIDEO (for image to video)
          const hasImages = imageUrls && (Array.isArray(imageUrls) ? imageUrls.length > 0 : true);

          body = {
            prompt: prompt,
            model: veoModelVariant || "veo3_fast",
            aspect_ratio: ratio || "16:9",
            generationType: hasImages ? "FIRST_AND_LAST_FRAMES_2_VIDEO" : "TEXT_2_VIDEO",
            enableTranslation: true,
          };

          if (hasImages) {
            body.imageUrls = Array.isArray(imageUrls) ? imageUrls : [imageUrls];
          }
        } else if (model.toLowerCase().includes("luma")) {
          endpoint = "/api/v1/luma/generate";
          if (apiOptions.ratio) body.aspect_ratio = apiOptions.ratio;
        } else if (model.toLowerCase().includes("wan")) {
          endpoint = "/api/v1/jobs/createTask";
          // Get duration and resolution from options (passed from Generate.tsx)
          const wanDuration = apiOptions.wanDuration || "10";
          const wanResolution = apiOptions.wanResolution || "720p";
          body = {
            model: "wan/2-6-text-to-video",
            input: {
              prompt: prompt,
              aspect_ratio: apiOptions.ratio === "9:16" ? "9:16" : "16:9",
              duration: wanDuration,
              resolution: wanResolution
            }
          };
        } else if (model.toLowerCase().includes("seedance") && apiOptions.imageUrls?.length > 0) {
          endpoint = "/api/v1/jobs/createTask";

          let imageUrls = [
            ...(apiOptions.firstFrameUrl ? [apiOptions.firstFrameUrl] : []),
            ...(apiOptions.imageUrls || []),
            ...(apiOptions.lastFrameUrl ? [apiOptions.lastFrameUrl] : []),
            ...(apiOptions.referenceImageUrls || [])
          ];
          if (imageUrls.some((url: string) => url.includes('supabase.co'))) {
            console.log('📤 Re-uploading image to Kie.ai temporary storage via proxy...');
            setStatusMessage(t("กำลังเตรียมไฟล์รูปภาพ...", "Preparing image file..."));
            setGenerationProgress(20);

            try {
              imageUrls = await Promise.all(imageUrls.map(async (url: string) => {
                if (!url.includes('supabase.co')) return url;
                const uploadResult = await uploadFileToKie(url, 'seedance-images');
                if (!uploadResult.success || !uploadResult.downloadUrl) {
                  throw new Error(uploadResult.error || 'Failed to upload image to Kie.ai');
                }
                return uploadResult.downloadUrl;
              }));
              console.log('✅ Images re-uploaded to Kie.ai:', imageUrls.length);
            } catch (uploadErr: any) {
              console.error('❌ Image re-upload failed:', uploadErr);
              throw new Error(t(
                "❌ ไม่สามารถเตรียมไฟล์รูปภาพได้\n\n💡 กรุณาลองอีกครั้ง หรือใช้รูปภาพไฟล์อื่น",
                "❌ Failed to prepare image file\n\n💡 Please try again or use a different image"
              ));
            }
          }

          let seedanceImageIndex = 0;
          if (apiOptions.firstFrameUrl) {
            apiOptions.firstFrameUrl = imageUrls[seedanceImageIndex++];
          }
          seedanceImageIndex += (apiOptions.imageUrls || []).length;
          if (apiOptions.lastFrameUrl) {
            apiOptions.lastFrameUrl = imageUrls[seedanceImageIndex++];
          }

          body = {
            model: "bytedance/seedance-2-fast",
            input: {
              prompt: finalPrompt,
              reference_image_urls: imageUrls,
              ...(apiOptions.firstFrameUrl ? { first_frame_url: apiOptions.firstFrameUrl } : {}),
              ...(apiOptions.lastFrameUrl ? { last_frame_url: apiOptions.lastFrameUrl } : {}),
              reference_video_urls: apiOptions.referenceVideoUrls || [],
              reference_audio_urls: apiOptions.referenceAudioUrls || [],
              generate_audio: apiOptions.generate_audio ?? true,
              resolution: apiOptions.resolution || "720p",
              aspect_ratio: apiOptions.ratio || "9:16",
              duration: Number(apiOptions.duration || apiOptions.n_frames || 15),
              web_search: apiOptions.web_search ?? false,
              nsfw_checker: apiOptions.nsfw_checker ?? true
            }
          };
          console.log('🎬 Seedance 2 Image-to-Video Request:', { duration: body.input.duration, ratio: body.input.aspect_ratio });
        } else if (model.includes("sora-2-image-to-video") || model.includes("sora-2-image-to-video-stable")) {
          endpoint = "/api/v1/jobs/createTask";

          // Handle image URL - re-upload to Kie.ai if from Supabase
          let imageUrls = apiOptions.imageUrls || [];
          if (imageUrls.length > 0 && imageUrls[0].includes('supabase.co')) {
            console.log('📤 Re-uploading image to Kie.ai temporary storage via proxy...');
            setStatusMessage(t("กำลังเตรียมไฟล์รูปภาพ...", "Preparing image file..."));
            setGenerationProgress(20);

            try {
              const uploadResult = await uploadFileToKie(imageUrls[0], 'image-to-video');
              if (!uploadResult.success || !uploadResult.downloadUrl) {
                throw new Error(uploadResult.error || 'Failed to upload image to Kie.ai');
              }
              imageUrls = [uploadResult.downloadUrl];
              console.log('✅ Image re-uploaded to Kie.ai:', imageUrls[0]);
            } catch (uploadErr: any) {
              console.error('❌ Image re-upload failed:', uploadErr);
              throw new Error(t(
                "❌ ไม่สามารถเตรียมไฟล์รูปภาพได้\n\n💡 กรุณาลองอีกครั้ง หรือใช้รูปภาพไฟล์อื่น",
                "❌ Failed to prepare image file\n\n💡 Please try again or use a different image"
              ));
            }
          }

          body = {
            model: "sora-2-image-to-video-stable",
            input: {
              prompt: finalPrompt,
              image_urls: imageUrls,
              aspect_ratio: apiOptions.ratio === "16:9" ? "landscape" : "portrait",
              n_frames: apiOptions.n_frames || "10",
              remove_watermark: apiOptions.remove_watermark ?? true,
              character_id: apiOptions.character_id || autoCharacterId || undefined
            }
          };
        } else if (model.includes("sora-2-characters")) {
          endpoint = "/api/v1/jobs/createTask";
          let fileUrl = apiOptions.videoUrl || (apiOptions.imageUrls && apiOptions.imageUrls[0]) || "";

          if (!fileUrl) {
            throw new Error("Character video URL is required. Please upload a video file.");
          }

          // Validate file URL format
          if (!fileUrl.startsWith('http')) {
            throw new Error("Invalid video URL format. URL must start with http:// or https://");
          }

          // If the URL is from Supabase, we need to re-upload to Kie.ai
          // because Kie.ai cannot fetch from Supabase storage directly
          if (fileUrl.includes('supabase.co')) {
            console.log('📤 Re-uploading video to Kie.ai temporary storage via proxy...');
            setStatusMessage(t("กำลังเตรียมไฟล์วิดีโอ...", "Preparing video file..."));
            setGenerationProgress(20);

            // Upload to Kie.ai via proxy (proxy handles fetching from Supabase)
            try {
              const uploadResult = await uploadFileToKie(fileUrl, 'character-videos');
              if (!uploadResult.success || !uploadResult.downloadUrl) {
                throw new Error(uploadResult.error || 'Failed to upload video to Kie.ai');
              }

              fileUrl = uploadResult.downloadUrl;
              console.log('✅ Video re-uploaded to Kie.ai:', fileUrl);
            } catch (uploadErr: any) {
              console.error('❌ Re-upload failed:', uploadErr);
              throw new Error(t(
                "❌ ไม่สามารถเตรียมไฟล์วิดีโอได้\n\n💡 กรุณาลองอีกครั้ง หรือใช้วิดีโอไฟล์อื่น",
                "❌ Failed to prepare video file\n\n💡 Please try again or use a different video"
              ));
            }
          }

          const charPrompt = apiOptions.character_prompt || prompt;
          const safetyInstr = apiOptions.safety_instruction || "";

          // Validate character prompt is not empty
          if (!charPrompt || charPrompt.trim().length === 0) {
            throw new Error("Character prompt is required.");
          }

          body = {
            model: "sora-2-characters",
            input: {
              character_prompt: charPrompt.trim(),
              safety_instruction: safetyInstr.trim(),
              character_file_url: [fileUrl] // Must be an array per API spec
            }
          };
          console.log('🧬 Sora Characters Request:', {
            fileUrl,
            character_prompt: charPrompt,
            safety_instruction: safetyInstr,
            fullBody: JSON.stringify(body, null, 2)
          });
        } else if (model.includes("grok-imagine/image-to-video")) {
          endpoint = "/api/v1/jobs/createTask";
          body = {
            model: "grok-imagine/image-to-video",
            input: {
              prompt: finalPrompt,
              image_urls: apiOptions.imageUrls || [],
              mode: apiOptions.mode || "normal",
              duration: apiOptions.duration || "6",
              aspect_ratio: apiOptions.ratio || "2:3",
              task_id: apiOptions.task_id || undefined,
              index: apiOptions.index !== undefined ? apiOptions.index : undefined
            }
          };
        } else if (model.includes("grok-imagine/text-to-video")) {
          endpoint = "/api/v1/jobs/createTask";
          body = {
            model: "grok-imagine/text-to-video",
            input: {
              prompt: finalPrompt,
              mode: apiOptions.mode || "normal",
              duration: apiOptions.duration || "6",
              aspect_ratio: apiOptions.ratio || "2:3"
            }
          };
        } else if (model.includes("grok-imagine/image-to-image")) {
          endpoint = "/api/v1/jobs/createTask";
          body = {
            model: "grok-imagine/image-to-image",
            input: {
              prompt: finalPrompt,
              image_urls: apiOptions.imageUrls || []
            }
          };
        } else if (model.includes("grok-imagine/text-to-image")) {
          endpoint = "/api/v1/jobs/createTask";
          body = {
            model: "grok-imagine/text-to-image",
            input: {
              prompt: finalPrompt,
              aspect_ratio: apiOptions.ratio || "3:2"
            }
          };
        } else if (model.toLowerCase().includes("seedance")) {
          endpoint = "/api/v1/jobs/createTask";
          const referenceImageUrls = [
            ...(apiOptions.firstFrameUrl ? [apiOptions.firstFrameUrl] : []),
            ...(apiOptions.lastFrameUrl ? [apiOptions.lastFrameUrl] : []),
            ...(apiOptions.referenceImageUrls || [])
          ];
          body = {
            model: "bytedance/seedance-2-fast",
            input: {
              prompt: finalPrompt,
              reference_image_urls: referenceImageUrls,
              ...(apiOptions.firstFrameUrl ? { first_frame_url: apiOptions.firstFrameUrl } : {}),
              ...(apiOptions.lastFrameUrl ? { last_frame_url: apiOptions.lastFrameUrl } : {}),
              reference_video_urls: apiOptions.referenceVideoUrls || [],
              reference_audio_urls: apiOptions.referenceAudioUrls || [],
              generate_audio: apiOptions.generate_audio ?? true,
              resolution: apiOptions.resolution || "720p",
              aspect_ratio: apiOptions.ratio || "9:16",
              duration: Number(apiOptions.duration || apiOptions.n_frames || 15),
              web_search: apiOptions.web_search ?? false,
              nsfw_checker: apiOptions.nsfw_checker ?? true
            }
          };
          console.log('🎬 Seedance 2 Text-to-Video Request:', { duration: body.input.duration, ratio: body.input.aspect_ratio });
        } else if (model.toLowerCase().includes("sora")) {
          endpoint = "/api/v1/jobs/createTask";
          body = {
            model: "sora-2-text-to-video-stable",
            input: {
              prompt: finalPrompt,
              aspect_ratio: apiOptions.ratio === "16:9" ? "landscape" :
                apiOptions.ratio === "1:1" ? "square" : "portrait",
              n_frames: apiOptions.n_frames || "10",
              remove_watermark: apiOptions.remove_watermark ?? true,
              character_id: apiOptions.character_id || autoCharacterId || undefined
            }
          };
          console.log('🎬 Sora Text-to-Video Stable Request:', { n_frames: apiOptions.n_frames, ratio: apiOptions.ratio });
        } else {
          endpoint = "/api/v1/video/generate";
        }
        // Only merge apiOptions for legacy video endpoints (not createTask models like Sora, Wan)
        // CreateTask models have specific body structure with nested input object
        const isCreateTaskModel = model.toLowerCase().includes("sora") ||
          model.toLowerCase().includes("seedance") ||
          model.toLowerCase().includes("wan") ||
          model.toLowerCase().includes("luma") ||
          model.toLowerCase().includes("grok-imagine") ||
          model.includes("sora-2-image-to-video") ||
          model.includes("sora-2-characters");
        if (!model.toLowerCase().includes("veo") && !isCreateTaskModel) {
          body = { ...body, ...apiOptions };
        }
      } else if (serviceType === "music") {
        endpoint = "/api/v1/jobs/createTask";
        const hasLyrics = apiOptions.lyrics && apiOptions.lyrics.trim().length > 0;
        const isInstrumental = apiOptions.instrumental === true;

        if (hasLyrics || isInstrumental) {
          // Custom Mode - for lyrics or instrumental
          body = {
            model: "ai-music-api/generate",
            input: {
              model: "V6",
              custom_mode: true,
              instrumental: isInstrumental,
              style: prompt, // use the main prompt as style
              title: prompt.substring(0, 80), // use first 80 chars as title
              ...(hasLyrics ? { lyrics: apiOptions.lyrics } : {}),
            },
          };
        } else {
          // Simple Mode - no lyrics, not instrumental
          body = {
            model: "ai-music-api/generate",
            input: {
              model: "V6",
              custom_mode: false,
              instrumental: false,
              prompt: prompt.substring(0, 500), // limit to 500 chars in simple mode
            },
          };
        }
      }

      if (callBackUrl && (endpoint.includes("/api/v1/jobs/createTask") || serviceType === "music")) {
        body = { ...body, callBackUrl };
      }

      console.log('🚀 Submitting to Kie.ai:', endpoint);
      setStatusMessage(t("ส่งงานไปที่เซิร์ฟเวอร์ AI...", "Sending task to AI server..."));
      setGenerationProgress(22);

      // Use proxy for sora-2-characters to avoid CORS issues
      const useProxy = model.includes('sora-2-characters');
      const submission = useProxy
        ? await submitKieTaskViaProxy(endpoint, body)
        : await submitKieTask(endpoint, apiKey, body);
      console.log('🚀 Kie.ai Submission Result:', JSON.stringify(submission, null, 2));

      if (submission.code && submission.code !== 200 && submission.code !== 0) {
        console.error('❌ Kie.ai API Error:', submission);
        const errorMsg = submission.msg || submission.message || `API Error ${submission.code}`;

        // Translate common Kie.ai errors
        if (errorMsg.toLowerCase().includes('server exception')) {
          throw new Error(t(
            "❌ ระบบ AI เกิดข้อผิดพลาด อาจเกิดจาก:\n• ไฟล์วิดีโอไม่รองรับ (ลอง MP4)\n• วิดีโอสั้นหรือยาวเกินไป (ควร 1-4 วินาที)\n• กรุณาลองอีกครั้ง",
            "❌ AI server error. This may be due to:\n• Unsupported video format (try MP4)\n• Video too short or too long (should be 1-4 seconds)\n• Please try again"
          ));
        }
        if (errorMsg.toLowerCase().includes('duration') && errorMsg.toLowerCase().includes('too long')) {
          throw new Error(t(
            "❌ วิดีโอยาวเกินไป!\n\n📏 ความยาวสูงสุด: 5 วินาที\n💡 คำแนะนำ: ตัดวิดีโอให้สั้นลง (1-4 วินาทีดีที่สุด)",
            "❌ Video is too long!\n\n📏 Maximum duration: 5 seconds\n💡 Tip: Trim your video to 1-4 seconds for best results"
          ));
        }
        if (errorMsg.toLowerCase().includes('duration') && errorMsg.toLowerCase().includes('too short')) {
          throw new Error(t(
            "❌ วิดีโอสั้นเกินไป!\n\n📏 ความยาวขั้นต่ำ: 1 วินาที\n💡 คำแนะนำ: ใช้วิดีโอความยาว 1-4 วินาที",
            "❌ Video is too short!\n\n📏 Minimum duration: 1 second\n💡 Tip: Use a video that is 1-4 seconds long"
          ));
        }
        if (errorMsg.toLowerCase().includes('file size') || errorMsg.toLowerCase().includes('too large')) {
          throw new Error(t(
            "❌ ไฟล์ใหญ่เกินไป!\n\n📦 ขนาดสูงสุด: 100 MB\n💡 คำแนะนำ: ลดขนาดไฟล์หรือใช้วิดีโอสั้นลง",
            "❌ File is too large!\n\n📦 Maximum size: 100 MB\n💡 Tip: Reduce file size or use a shorter video"
          ));
        }
        if (errorMsg.toLowerCase().includes('format') || errorMsg.toLowerCase().includes('unsupported')) {
          throw new Error(t(
            "❌ รูปแบบไฟล์ไม่รองรับ!\n\n✅ รองรับ: MP4, MOV, WEBM, M4V, AVI\n💡 คำแนะนำ: แปลงไฟล์เป็น MP4 แล้วลองใหม่",
            "❌ Unsupported file format!\n\n✅ Supported: MP4, MOV, WEBM, M4V, AVI\n💡 Tip: Convert to MP4 and try again"
          ));
        }
        if (errorMsg.toLowerCase().includes('character') && errorMsg.toLowerCase().includes('not found')) {
          throw new Error(t(
            "❌ ไม่พบตัวละคร!\n\n💡 ตัวละครอาจหมดอายุแล้ว (3 วัน)\n🔄 กรุณาสร้างตัวละครใหม่",
            "❌ Character not found!\n\n💡 Character may have expired (3 days)\n🔄 Please create a new character"
          ));
        }
        if (errorMsg.toLowerCase().includes('fetch failed') || errorMsg.toLowerCase().includes('access settings')) {
          throw new Error(t(
            "❌ ไม่สามารถเข้าถึงไฟล์วิดีโอได้!\n\n💡 คำแนะนำ: ลองอัปโหลดวิดีโอใหม่อีกครั้ง\n🔄 หากยังไม่ได้ กรุณาแจ้ง Admin",
            "❌ Cannot access video file!\n\n💡 Tip: Try uploading the video again\n🔄 If issue persists, contact Admin"
          ));
        }
        throw new Error(errorMsg);
      }

      const taskId = submission.taskId ||
        submission.id ||
        (typeof submission.data === 'string' ? submission.data : (submission.data?.taskId || submission.data?.id || submission.data?.task_id));

      // console.log('🆔 Extracted Task ID:', taskId);

      if (!taskId) {
        console.error('❌ Failed to extract taskId from:', submission);
        throw new Error(t("ไม่ได้รับ Task ID จากระบบ AI", "No taskId received from Kie.ai"));
      }

      // 5. Save taskId to database immediately
      await updateLogStatus(logId, "processing", taskId);

      // Show temporary message
      const estimatedTime = serviceType === "video" ? t("ประมาณ 6 นาที", "~6 minutes") : t("ประมาณ 2-3 นาที", "~2-3 minutes");
      setStatusMessage(t("กำลังสร้างเนื้อหา (โปรดรอสักครู่)...", "Generating content (please wait)..."));
      setGenerationProgress(25);
      toast.info(t(`🎬 กำลังสร้าง... (${estimatedTime})\n\nไปทำอย่างอื่นรอได้ครับ กลับมาเช็กทีหลังที่หน้า Dashboard`, `🎬 Generating... (${estimatedTime})\n\nYou can do other things. Check back later on Dashboard`), { duration: 8000 });

      // Polling for all types (photo/video/music)
      let resultUrl = "";
      let attempts = 0;
      let retryCount = 0;
      const maxRetries = 1; // Retry once on error

      // Higher timeout for music/video (approx 30 mins)
      const maxAttempts = (serviceType === "music" || serviceType === "video") ? 120 : 40;
      const pollInterval = 15000; // 15 seconds between polls

      // Wait longer before first poll for music/video
      if (serviceType === "music" || serviceType === "video") {
        const waitTime = serviceType === "video" ? 30000 : 60000;
        const message = serviceType === "video"
          ? t("🎬 กำลังสร้างวิดีโอ... รอสักครู่ระบบจะตรวจสอบสถานะให้", "🎬 Generating video... system will check status shortly")
          : t("🎵 กำลังสร้างเพลง รอประมาณ 1-2 นาที", "🎵 Generating music, please wait 1-2 minutes");

        // console.log(`${serviceType === 'video' ? '🎬 Video' : '🎵 Music'} generation - waiting ${waitTime / 1000}s before first poll...`);
        toast.info(message, { duration: 5000 });
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }

      let kiePollAttempts = 0;

      while (attempts < maxAttempts) {
        // 1. Check our own DB first (high priority for callback results)
        const { data: currentLog, error: dbError } = await supabase
          .from("generation_logs")
          .select("status, result_url")
          .eq("id", logId)
          .single();

        if (dbError) {
          console.warn('⚠️ DB Poll Error:', dbError);
        }

        if (currentLog?.status === "completed" && currentLog?.result_url?.startsWith("http")) {
          // console.log('✅ Found result in DB via callback!');
          setGenerationProgress(100);
          setStatusMessage(t("สร้างสำเร็จ!", "Generation complete!"));
          resultUrl = currentLog.result_url;
          break;
        } else if (currentLog?.status === "failed") {
          throw new Error(t("การสร้างล้มเหลว (แจ้งจากระบบ)", "Generation failed (reported by system)"));
        }

        // Simulating progress
        setGenerationProgress(prev => {
          if (prev >= 98) return 98;
          // Slowly increment based on service type
          const increment = serviceType === "video" ? 1.5 : 3;
          return Math.min(prev + increment, 98);
        });

        if (attempts > 5) {
          setStatusMessage(t("กำลังประมวลผลวิดีโอ...", "Processing video..."));
        }
        if (attempts > 15) {
          setStatusMessage(t("กำลังเข้ารหัสและปรับแต่ง...", "Encoding and refining..."));
        }

        // 2. Poll Kie AI directly (Restricted to max 3 times to avoid rate limits)
        // Poll at: 0s (start), 150s (2.5m), 300s (5m) given 15s interval
        const shouldPollKie = taskId && kiePollAttempts < 3 && (attempts === 0 || attempts % 10 === 0);

        if (shouldPollKie) {
          try {
            kiePollAttempts++;
            // console.log(`🔍 Polling Kie AI (${kiePollAttempts}/3) - Attempt ${attempts}...`);

            // Determine correct poll type
            let pollType = "modify";
            if (serviceType === "music") {
              pollType = "suno";
            } else if (
              model.toLowerCase().includes("sora") ||
              model.toLowerCase().includes("seedance") ||
              model.toLowerCase().includes("grok") ||
              model.toLowerCase().includes("seedream") ||
              model.toLowerCase().includes("gpt-image") ||
              model.toLowerCase().includes("gpt4o") ||
              model.toLowerCase().includes("flux") ||
              model.toLowerCase().includes("kontext") ||
              model.toLowerCase().includes("wan") ||
              model.toLowerCase().includes("luma") ||
              model.toLowerCase().includes("veo") ||
              model.toLowerCase().includes("nanobana") ||
              model.toLowerCase().includes("nano banana")
            ) {
              pollType = "market";
            }

            const pollResult = await pollKieTask(taskId, apiKey, pollType);

            // DEBUG: Full response
            // console.log('📦 Full Kie API Response:', JSON.stringify(pollResult, null, 2));

            // Extract status and URL from Kie AI response
            const response = pollResult.data || pollResult;
            const failMsg = response.failMsg || response.msg || pollResult.msg;
            // console.log('📄 Response object:', JSON.stringify(response, null, 2));

            const resData = response.response || response.result;
            // console.log('📄 resData (response/result):', JSON.stringify(resData, null, 2));

            let kieUrl: string | null = null;
            let kieStatus = (response.state ?? response.status ?? response.taskStatus);
            // console.log('📊 kieStatus:', kieStatus);

            // Normalize status
            if (typeof kieStatus === 'number') {
              if (kieStatus === 3) kieStatus = "success";
              else if (kieStatus === 4) kieStatus = "failed";
            }

            // Try multiple extraction paths
            // Check for sunoData array first (Suno music specific - from polling)
            if (resData?.sunoData && Array.isArray(resData.sunoData) && resData.sunoData.length > 0) {
              // console.log('🎵 Extracting from sunoData array...');
              const tracks = resData.sunoData.map((item: any) => {
                // Prefer audio URL with .mp3 extension for download compatibility
                const audioUrl = item.audioUrl || item.audio_url;
                const streamUrl = item.streamAudioUrl || item.stream_audio_url;
                // Use audio URL if available (has .mp3), otherwise fallback to stream
                const finalAudio = (audioUrl && audioUrl.includes('.mp3')) ? audioUrl : (audioUrl || streamUrl || item.url);
                return {
                  audio: finalAudio,
                  image: item.imageUrl || item.image_url,
                  title: item.title || "",
                  duration: item.duration || 0
                };
              }).filter((t: any) => t.audio && t.audio.startsWith("http"));

              if (tracks.length > 0) {
                kieUrl = JSON.stringify({ tracks });
              }
              // console.log('🔗 Extracted kieUrl from sunoData:', kieUrl);
            }
            // Check for callback format (data array nested in response.data)
            else if (resData?.data && Array.isArray(resData.data) && resData.data.length > 0) {
              // console.log('🎵 Extracting from callback data array...');
              const tracks = resData.data.map((item: any) => {
                // Callback format uses snake_case: audio_url, stream_audio_url, image_url
                const audioUrl = item.audio_url || item.audioUrl;
                const streamUrl = item.stream_audio_url || item.streamAudioUrl;
                const finalAudio = (audioUrl && audioUrl.includes('.mp3')) ? audioUrl : (audioUrl || streamUrl || item.url);
                return {
                  audio: finalAudio,
                  image: item.image_url || item.imageUrl,
                  title: item.title || "",
                  duration: item.duration || 0
                };
              }).filter((t: any) => t.audio && t.audio.startsWith("http"));

              if (tracks.length > 0) {
                kieUrl = JSON.stringify({ tracks });
              }
              // console.log('🔗 Extracted kieUrl from callback data:', kieUrl);
            }
            else if (Array.isArray(resData) && resData.length > 0) {
              // console.log('🎵 Extracting from array...');
              kieUrl = resData
                .map((item: any) => item.audioUrl || item.audio_url || item.url || item.video_url)
                .filter((u: any) => typeof u === "string" && u.startsWith("http"))
                .join(",");
              // console.log('🔗 Extracted kieUrl from array:', kieUrl);
            } else if (resData && typeof resData === 'object') {
              // console.log('🎵 Extracting from object...');
              kieUrl = resData.audioUrl || resData.audio_url || resData.url || resData.video_url;
              // console.log('🔗 Extracted kieUrl from object:', kieUrl);
            }

            // Fallback 1: Direct URL fields on response
            if (!kieUrl) {
              kieUrl = response.url || response.resultUrl || response.audio_url || response.video_url;
              // console.log('🔗 Fallback 1 (response.url/resultUrl):', kieUrl);
            }

            // Fallback 2: resultJson parsing
            if (!kieUrl && (pollResult.resultJson || response.resultJson)) {
              const rj = pollResult.resultJson || response.resultJson;
              // console.log('📄 resultJson:', rj);
              try {
                const parsed = typeof rj === 'string' ? JSON.parse(rj) : rj;
                if (typeof parsed === 'string' && parsed.startsWith('http')) {
                  kieUrl = parsed;
                } else if (parsed?.resultObject?.character_id) {
                  kieUrl = `character_id:${parsed.resultObject.character_id}`;
                } else if (parsed?.resultObject) {
                  kieUrl = JSON.stringify(parsed);
                } else if (Array.isArray(parsed) && parsed.length > 0) {
                  kieUrl = parsed.find((u: any) => typeof u === 'string' && u.startsWith('http')) || null;
                } else if (parsed?.resultUrls?.[0]) {
                  kieUrl = parsed.resultUrls[0];
                } else if (parsed?.url) {
                  kieUrl = parsed.url;
                }
                // console.log('🔗 Fallback 2 (resultJson):', kieUrl);
              } catch (e) {
                console.warn('⚠️ resultJson parse error:', e);
              }
            }

            // console.log('🔗 Final kieUrl:', kieUrl, '| kieStatus:', kieStatus);

            // Check for completion - handle various status formats
            const isSuccess = kieUrl ||
              kieStatus === "success" ||
              kieStatus === "SUCCESS" ||
              kieStatus === "completed" ||
              kieStatus === "COMPLETED" ||
              kieStatus === "FIRST_SUCCESS" ||
              kieStatus === "first_success";

            if (isSuccess) {
              if (kieUrl) {
                // console.log('✅ Found result via direct polling! Updating DB...');
                setGenerationProgress(100);
                setStatusMessage(t("สร้างสำเร็จ!", "Generation complete!"));
                resultUrl = kieUrl;
                // Update DB since callback might have failed
                const updated = await updateLogStatus(logId, "completed", resultUrl);
                // console.log('📝 DB Update result:', updated);
                break;
              } else {
                // console.log('⏳ Status is success but URL not available yet...');
              }
            } else if (kieStatus === "failed" || kieStatus === "FAILED" || kieStatus === "fail" || kieStatus === "error" || kieStatus === "ERROR" || kieStatus === "GENERATE_AUDIO_FAILED") {
              // Retry once on error
              if (retryCount < maxRetries) {
                retryCount++;
                // console.log(`🔄 Generation failed, retrying... (${retryCount}/${maxRetries})`);
                toast.info(t("🔄 เกิดข้อผิดพลาด กำลังลองใหม่...", "🔄 Error occurred, retrying..."), { duration: 3000 });
                await new Promise(resolve => setTimeout(resolve, 10000));
                continue;
              }
              throw new Error(failMsg ? `${t("การสร้างล้มเหลว", "Generation failed")}: ${failMsg}` : t("การสร้างล้มเหลว", "Generation failed"));
            }
          } catch (pollErr: any) {
            console.warn('⚠️ Poll Error:', pollErr?.message || pollErr);
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;
      }

      if (!resultUrl) throw new Error("Generation timed out");

      // Veo Full HD 1080p upscale - request after base generation succeeds
      if (apiOptions.veoFullHD && model.toLowerCase().includes("veo") && taskId) {
        try {
          setStatusMessage(t("กำลังอัปสเกลเป็น 1080p...", "Upscaling to 1080p..."));
          setGenerationProgress(85);

          const baseUrl = "https://api.kie.ai";
          let hdUrl: string | null = null;
          let hdAttempts = 0;
          const maxHdAttempts = 10;

          // Wait before first attempt
          await new Promise(resolve => setTimeout(resolve, 15000));

          while (hdAttempts < maxHdAttempts) {
            hdAttempts++;
            try {
              const hdResponse = await fetch(`${baseUrl}/api/v1/veo/get-1080p-video?taskId=${taskId}`, {
                method: "GET",
                headers: { "Authorization": `Bearer ${apiKey}` },
              });

              if (hdResponse.ok) {
                const hdData = await hdResponse.json();
                if (hdData.code === 200 && hdData.data?.resultUrl) {
                  hdUrl = hdData.data.resultUrl;
                  break;
                }
              }
            } catch (hdErr) {
              console.warn(`1080p attempt ${hdAttempts} failed:`, hdErr);
            }

            setGenerationProgress(85 + (hdAttempts / maxHdAttempts) * 10);
            await new Promise(resolve => setTimeout(resolve, 20000));
          }

          if (hdUrl) {
            resultUrl = hdUrl;
            console.log('✅ 1080p upscale successful');
            toast.success(t("อัปสเกล 1080p สำเร็จ!", "1080p upscale complete!"));
          } else {
            console.warn('⚠️ 1080p upscale failed, using original quality');
            toast.info(t("ไม่สามารถอัปสเกล 1080p ได้ ใช้คุณภาพเดิม", "1080p upscale unavailable, using original quality"));
          }
        } catch (hdError) {
          console.warn('⚠️ 1080p upscale error:', hdError);
        }
      }

      await updateLogStatus(logId, "completed", resultUrl);
      await refreshProfile();
      toast.success(t(`สร้างสำเร็จ! หัก ${creditsRequired} เครดิต`, `Generated successfully! ${creditsRequired} credits deducted`));

      setIsGenerating(false);
      return { success: true, logId, resultUrl };
    } catch (error: any) {
      console.error("Generation error:", error);
      setIsGenerating(false);

      // Extract error message for storage
      const rawErrorMessage = error.message || t("เกิดข้อผิดพลาดในการสร้าง", "Generation failed");

      // Auto refund on failure
      if (logId) {
        // console.log(`💰 Refunding credits due to failure for log: ${logId}`);
        await refundCredits(logId);
        await updateLogStatus(logId, "failed", undefined, rawErrorMessage);
      }

      // Translate common API errors to Thai for display
      let errorMessage = rawErrorMessage;

      if (error.message?.toLowerCase().includes("cameo") || error.message?.toLowerCase().includes("not allowed to access")) {
        errorMessage = t(
          "❌ ไม่สามารถเข้าถึงตัวละคร/Cameo ได้\n\nกรุณาตั้งค่าตัวละครเป็น Public ก่อนใช้งาน\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ Cannot access character/Cameo\n\nPlease set your character to Public before using.\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("character video url is required") || error.message?.toLowerCase().includes("upload a video")) {
        errorMessage = t(
          "❌ กรุณาอัปโหลดวิดีโอตัวอย่างตัวละคร (1-4 วินาที)\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ Please upload a character video clip (1-4 seconds)\n\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("character prompt is required")) {
        errorMessage = t(
          "❌ กรุณากรอกลักษณะตัวละคร\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ Please enter character description\n\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("insufficient") || error.message?.toLowerCase().includes("balance")) {
        errorMessage = t(
          "❌ ระบบ AI ทำงานผิดพลาด กรุณาแจ้ง Admin\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ AI system error. Please contact Admin.\n\n💰 Credits have been refunded"
        );
      } else if (error.message?.toLowerCase().includes("rate limit") || error.message?.toLowerCase().includes("too many")) {
        errorMessage = t(
          "❌ ระบบ AI กำลังทำงานหนัก กรุณารอสักครู่แล้วลองใหม่\n\n💰 เครดิตถูกคืนเรียบร้อยแล้ว",
          "❌ AI system is busy. Please wait and try again.\n\n💰 Credits have been refunded"
        );
      } else if (logId) {
        // Add refund notice for other errors
        errorMessage = `${errorMessage}\n\n💰 ${t("เครดิตถูกคืนเรียบร้อยแล้ว", "Credits have been refunded")}`;
      }

      toast.error(errorMessage, { duration: 8000 });
      return { success: false, error: error.message || "Generation failed" };
    }
  };

  return {
    generate,
    isGenerating,
    generationProgress,
    statusMessage,
    checkCredits,
    deductCredits,
    refundCredits,
    createLog,
    updateLogStatus,
    userCredits: profile?.credits ?? 0,
  };
};
